import { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { message } from "antd";
import { useHistory } from "react-router-dom";
import { helper } from "@src/controls/controlHelper";

/**
 * Id trang CMS gắn `CreateTicketFormCtrl` (layout trang 120).
 * Khác trang với {@link CLONE_TICKET_FORM_PAGE_ID}; hai trang chỉ trùng **danh sách API** (cấu hình backend).
 */
export const DEFAULT_TICKET_FORM_PAGE_ID = 120;

/**
 * Id trang CMS gắn `CloneTicketFormCtrl` (layout trang 144).
 * Khác trang với {@link DEFAULT_TICKET_FORM_PAGE_ID}; hai trang chỉ trùng **danh sách API** (cấu hình backend).
 */
export const CLONE_TICKET_FORM_PAGE_ID = 144;

export type MetaItem = { name: string; value: string };

export type EventForm = {
  image?: string;
  banner?: string[];
  description?: string;
  nameEvent: string;
  startDateEvent?: Dayjs | number | string;
  endDateEvent?: Dayjs | number | string;
  presaleStart?: Dayjs | number | string;
  presaleEnd?: Dayjs | number | string;
  stadiumId?: number;
  matchId?: number; // nếu không phải vé mùa
  maxItemsPerOrder?: number;
  salesChannels?: Array<"ONLINE" | "INVITATION">;
  reservationTime?: number;
  status?: boolean;
  checkInTime?: Dayjs | number | string;
  endCheckInTime?: Dayjs | number | string;
  isTicketsVaidForMultipleSeasons?: boolean;
  tournamentIds?: number[];
  metaProperties?: MetaItem[];
  // seatPlanId?: number;
  ticketLayoutId?: number;
};

export type TicketItem = {
  id?: number;
  name: string;
  originPrice: number;
  price: number;
  image?: string;
  description?: string;
  startDateSell?: Dayjs | number | string;
  endDateSell?: Dayjs | number | string;
  maxItemsPerOrder?: number;
  minItemsPerOrder?: number;
  status: boolean;
  quantity: number;
  metaData?: MetaItem[];
};

export const defaultEventMeta: MetaItem[] = [
  { name: "HOME", value: "" },
  { name: "VISITOR", value: "" },
  { name: "LEAGUE", value: "" },
];

export const defaultTicketMeta: MetaItem[] = [
  { name: "GATE", value: "" },
  { name: "ZONE", value: "" },
  { name: "FLOOR", value: "" },
  { name: "SEAT", value: "" },
];

const defaultTicket: TicketItem = {
  name: "",
  originPrice: 0,
  price: 0,
  image: "",
  description: "",
  startDateSell: undefined,
  endDateSell: undefined,
  maxItemsPerOrder: 10,
  minItemsPerOrder: 1,
  status: true,
  quantity: 0,
  metaData: [...defaultTicketMeta],
};

export type UseTicketFormOptions = {
  /**
   * true: URL/query vẫn có id/eventId để load dữ liệu cũ (find_event_ticket_info),
   * nhưng khi Lưu luôn gọi create và không gửi id vé/sự kiện cũ (sao chép).
   */
  cloneFromExisting?: boolean;
  /**
   * Id trang CMS hiện tại (mỗi màn một page: 120 create, 144 clone).
   * Layout/schema trang có thể khác; `helper.getPage` / `callPageApi` phải dùng đúng id trang đó.
   * Giá trị cần khớp `pageId` truyền xuống các section (layout vé, v.v.).
   */
  pageId?: number;
};

export const useTicketForm = (query?: any, options?: UseTicketFormOptions) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [init, setInit] = useState(false);
  const isSubmittingRef = useRef(false);
  const cloneFromExisting = Boolean(options?.cloneFromExisting);
  const resolvedPageId = options?.pageId ?? DEFAULT_TICKET_FORM_PAGE_ID;
  // Stable identifiers for edit mode (load prefill); clone vẫn dùng các id này để fetch
  const editId = query?.id;
  const editTicketId = query?.ticketId;
  const editEventId = query?.eventId;
  const isEdit = useMemo(() => Boolean(editId || editTicketId || editEventId), [editId, editTicketId, editEventId]);

  const initialValues = useMemo(() => ({
    envent: {
      image: "",
      banner: [],
      description: "",
      nameEvent: "",
      startDateEvent: undefined,
      endDateEvent: undefined,
      presaleStart: undefined,
      presaleEnd: undefined,
      stadiumId: undefined,
      matchId: undefined,
      maxItemsPerOrder: 10,
      salesChannels: ["ONLINE"] as Array<"ONLINE" | "INVITATION">,
      reservationTime: 300,
      status: true,
      checkInTime: undefined,
      endCheckInTime: undefined,
      isTicketsVaidForMultipleSeasons: false,
      tournamentIds: [],
      metaProperties: defaultEventMeta,
      // seatPlanId: undefined,
      ticketLayoutId: undefined,
    } as EventForm,
    ticket: [
      { ...defaultTicket },
    ],
    metaData: [],
  }), []);

  useEffect(() => {
    // TODO: load data for edit mode when API is available
    setInit(true);
  }, [editId]);

  const toUnix = (v?: Dayjs | number | string) => {
    if (typeof v === "number") return v;
    if (!v) return undefined;
    if (typeof v === "string") {
      const d = dayjs(v);
      return d.isValid() ? d.valueOf() : undefined;
    }
    return (v as Dayjs).valueOf();
  };

  const toNumberSafe = (v: any): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const onFinish = async (values: { envent?: EventForm; ticket?: TicketItem[] }) => {
    if (isSubmittingRef.current) {
      return;
    }
    try {
      isSubmittingRef.current = true;
      setLoading(true);
      const ev = (values && values.envent) ? values.envent : ({} as EventForm);
      const payload = {
        envent: {
          ...ev,
          startDateEvent: toUnix(ev.startDateEvent),
          endDateEvent: toUnix(ev.endDateEvent),
          presaleStart: toUnix(ev.presaleStart),
          presaleEnd: toUnix(ev.presaleEnd),
          checkInTime: toUnix(ev.checkInTime),
          endCheckInTime: toUnix(ev.endCheckInTime),
          isTicketsVaidForMultipleSeasons: Boolean(ev.isTicketsVaidForMultipleSeasons),
          tournamentIds: Array.isArray(ev.tournamentIds) ? ev.tournamentIds : [],
          metaProperties:
            Array.isArray(ev.metaProperties) && ev.metaProperties.length > 0
              ? ev.metaProperties
              : defaultEventMeta,
        },
        ticket: (values?.ticket ?? []).filter(Boolean).map((t) => {
          const normalizedTicketMeta = (Array.isArray(t?.metaData) && t.metaData.length > 0
            ? t.metaData
            : defaultTicketMeta).map((m) => {
              const nameUpper = String(m?.name || '').toUpperCase();
              if (nameUpper === 'GATE') {
                return { name: m?.name, value: String(m?.value ?? ''), description: (m as any)?.description } as any;
              }
              return { name: m?.name, value: String((m as any)?.value ?? ''), description: (m as any)?.description } as any;
            });
          return {
            ...t,
            id: toNumberSafe((t as any)?.id),
            startDateSell: toUnix(t?.startDateSell),
            endDateSell: toUnix(t?.endDateSell),
            metaData: normalizedTicketMeta.map((x: any) => {
              const { __gid, ...rest } = x || {};
              return rest;
            }),
          };
        }),
      };

      // Normalize numeric ids and strip null/invalids from envent
      const enventAny: any = payload.envent as any;
      const numericStadiumId = toNumberSafe(enventAny.stadiumId);
      if (numericStadiumId !== undefined) enventAny.stadiumId = numericStadiumId; else delete enventAny.stadiumId;
      const numericMatchId = toNumberSafe(enventAny.matchId);
      if (numericMatchId !== undefined) enventAny.matchId = numericMatchId; else delete enventAny.matchId;
      const numericTicketLayoutId = toNumberSafe(enventAny.ticketLayoutId);
      if (numericTicketLayoutId !== undefined) enventAny.ticketLayoutId = numericTicketLayoutId; else delete enventAny.ticketLayoutId;
      const numericSeatPlanId = toNumberSafe(enventAny.seatPlanId);
      if (numericSeatPlanId !== undefined) enventAny.seatPlanId = numericSeatPlanId; else delete enventAny.seatPlanId;

      const pageInfo = await helper.getPage(resolvedPageId);

      const eventIdRef = editEventId || editId;
      const ticketIdRef = editTicketId;
      const shouldCallUpdateApi = isEdit && !cloneFromExisting;
      const apiName = shouldCallUpdateApi ? "update_event_ticket" : "create_envent_ticket";
      const numEventId = toNumberSafe(eventIdRef);
      const numTicketId = toNumberSafe(ticketIdRef);
      let requestPayload: any = shouldCallUpdateApi
        ? { ...payload, eventId: numEventId, id: numEventId, ticketId: numTicketId }
        : payload;

      if (cloneFromExisting) {
        if (Array.isArray(requestPayload.ticket)) {
          requestPayload = {
            ...requestPayload,
            ticket: requestPayload.ticket.map((ticketRow: any) => {
              const { id: _discardedTicketId, ...ticketWithoutId } = ticketRow || {};
              return ticketWithoutId;
            }),
          };
        }
        const enventRow = requestPayload.envent || {};
        const { id: _discardedEventId, ...enventWithoutId } = enventRow;
        requestPayload = { ...requestPayload, envent: enventWithoutId };
      }

      const rs: any = await helper.callPageApi(
        pageInfo,
        apiName,
        requestPayload
      );

      if ((rs?.status && rs.status !== 200) || (rs?.data && rs.data.code !== 0)) {
        throw rs?.data || rs;
      }
      helper.alert(
        shouldCallUpdateApi ? "Cập nhật sự kiện và vé thành công" : "Tạo sự kiện và vé thành công",
        "success"
      );
      history.goBack();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log(error);
      message.error(error?.message || "Không thể lưu, vui lòng thử lại");
      helper.alert("Lưu thất bại", "error");
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const loadMetaDataForEdit = async (): Promise<{
    eventMeta?: MetaItem[];
    ticketMeta?: MetaItem[];
  }> => {
    try {
      const pageInfo = await helper.getPage(resolvedPageId);
      const rs: any = await helper.callPageApi(pageInfo, 'find_meta_data', {
        ticketId: query?.ticketId || query?.id,
        eventId: query?.eventId,
      });
      const data = rs?.data?.data ?? rs?.data ?? rs ?? {};
      const eventMeta: MetaItem[] = data.eventMeta || data.event || [];
      const ticketMeta: MetaItem[] = data.ticketMeta || data.ticket || [];
      return { eventMeta, ticketMeta };
    } catch (e) {
      return {};
    }
  };

  const loadEventTicketInfo = async (): Promise<{
    envent?: EventForm;
    ticket?: TicketItem[];
    eventMeta?: MetaItem[];
    ticketMeta?: MetaItem[];
  }> => {
    try {
      const pageInfo = await helper.getPage(resolvedPageId);

      const rs: any = await helper.callPageApi(pageInfo, 'find_event_ticket_info', {
        eventId: query?.eventId || query?.id,
      });

      const data = rs?.data?.data ?? rs?.data ?? rs ?? {};
      const envent = data.envent || {};
      const ticket = Array.isArray(data.ticket) ? data.ticket : [];
      const eventMeta: MetaItem[] = data.eventMeta || data.event || envent?.metaProperties || [];
      const ticketMeta: MetaItem[] = data.ticketMeta || [];
      return { envent, ticket, eventMeta, ticketMeta };
    } catch (e) {
      return {};
    }
  };

  return {
    PAGE_ID: resolvedPageId,
    loading,
    init,
    initialValues,
    defaultTicket,
    onFinish,
    loadMetaDataForEdit,
    loadEventTicketInfo,
  };
};


